/**
 * ContractList — Contract history per staff member
 */

import { useEffect } from 'react';
import type { Contract } from '../../stores/workforce-store';
import { useWorkforceStore } from '../../stores';
import { ContractAlerts } from './ContractAlerts';

const statusStyles: Record<string, string> = {
  concept: 'bg-gray-100 text-gray-700',
  werkgever_getekend: 'bg-blue-100 text-blue-700',
  verstuurd: 'bg-amber-100 text-amber-700',
  getekend: 'bg-emerald-100 text-emerald-700',
  verlopen: 'bg-red-100 text-red-700',
  'beëindigd': 'bg-gray-100 text-gray-500',
};

const statusLabels: Record<string, string> = {
  concept: 'concept',
  werkgever_getekend: 'werkgever getekend',
  verstuurd: 'verstuurd',
  getekend: 'getekend',
  verlopen: 'verlopen',
  'beëindigd': 'beëindigd',
};

const typeLabels: Record<string, string> = {
  regulier: 'Regulier',
  jaaruren: 'Jaaruren (urennorm per jaar)',
  'vast-salaris': 'Vast salaris (geen urenregistratie)',
  '0-uren-allin': '0-uren (all-in)',
  '0-uren-opbouw': '0-uren (opbouw)',
  'min-max': 'Min/max (garantie-uren)',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  const months = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

interface ContractListProps {
  staffId: string;
  onNewContract: () => void;
  onViewContract: (contract: Contract) => void;
}

export function ContractList({ staffId, onNewContract, onViewContract }: ContractListProps) {
  const { contracts, isLoadingContracts, fetchContracts, sendContract, downloadContractPdf, deleteContract } = useWorkforceStore();

  useEffect(() => {
    fetchContracts(staffId);
  }, [staffId, fetchContracts]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-main">Contracten</h3>
        <button
          onClick={onNewContract}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 transition-colors"
        >
          + Nieuw contract
        </button>
      </div>

      {/* Niet getekend + ketenregeling (zelfde telling als de API-poort) */}
      <ContractAlerts contracts={contracts} />

      {isLoadingContracts ? (
        <div className="text-center py-8 text-muted text-sm">Laden...</div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-8 text-muted text-sm">Nog geen contracten</div>
      ) : (
        <div className="space-y-2">
          {contracts.map(contract => (
            <div
              key={contract.id}
              className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-200 hover:border-indigo-300 transition-colors cursor-pointer"
              onClick={() => onViewContract(contract)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-main">{contract.contract_number}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyles[contract.status] || 'bg-gray-100 text-gray-700'}`}>
                    {statusLabels[contract.status] || contract.status}
                  </span>
                  {contract.stilzwijgend_verlengd && (
                    <span
                      className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700"
                      title="Voortzetting van rechtswege (art. 7:668 lid 4 BW): doorgewerkt na de einddatum, zelfde voorwaarden, geen handtekening"
                    >
                      stilzwijgend verlengd
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted">
                  <span>{typeLabels[contract.employment_type] || contract.employment_type}</span>
                  <span>·</span>
                  <span>{contract.contract_type === 'bepaald' ? 'Bepaald' : 'Onbepaald'}</span>
                  <span>·</span>
                  <span>{formatDate(contract.start_date)}{contract.end_date ? ` — ${formatDate(contract.end_date)}` : ''}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                {contract.status === 'werkgever_getekend' && (
                  <button
                    onClick={() => sendContract(contract.id)}
                    className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    title="Verstuur ter ondertekening"
                  >
                    Verstuur
                  </button>
                )}
                <button
                  onClick={() => downloadContractPdf(contract.id)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download PDF"
                >
                  PDF
                </button>
                {contract.status === 'concept' && (
                  <button
                    onClick={() => {
                      if (confirm('Weet je zeker dat je dit contract wilt verwijderen?')) {
                        deleteContract(contract.id);
                      }
                    }}
                    className="px-2 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Verwijderen"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
